import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Phone, Clock, MapPin } from 'lucide-react'
import { config, useClientConfig } from '@workspace/public-utils'

interface CompanyInfo {
  name: string | null
  contact_email: string | null
  contact_number: string | null
  contact_number_secondary: string | null
  working_hours: string | null
  address: string | null
  business_registration_number?: string | null
  ecommerce_registration_number?: string | null
  owner?: string | null
  chief_privacy_officer?: string | null
  bank_infos?: Array<{
    bank_name: string
    account_number: string
    account_holder: string
    bank_logo_url: string
  }>
  extra_bottom_images?: string[]
}

const StarFooter = () => {
  const clientConfig = useClientConfig()
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/v1/public/company-info`)
        if (response.ok) {
          const data = await response.json()
          setCompanyInfo(data)
        }
      } catch (error) {
        console.error('Failed to fetch company info:', error)
      }
    }
    fetchCompanyInfo()
  }, [])

  const uniqueBanks = companyInfo?.bank_infos?.filter(
    (b, i, self) => i === self.findIndex((t) => t.account_number === b.account_number)
  )

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 transition-colors">
      {/* Bank Account Section */}
      {uniqueBanks && uniqueBanks.length > 0 && (
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h3 className="text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-6 text-center">
              무통장 입금 안내
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {uniqueBanks.map((b, i) => (
                <div
                  key={`${b.account_number}-${i}`}
                  className="flex items-center gap-4 bg-gray-50 dark:bg-gray-800 rounded-xl px-6 py-4 min-w-[280px]"
                >
                  {b.bank_logo_url && (
                    <img src={b.bank_logo_url} alt={b.bank_name} className="w-10 h-10 object-contain" />
                  )}
                  <div>
                    <p className="font-bold text-star-navy dark:text-white text-sm">{b.bank_name}</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm font-mono">{b.account_number}</p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs">예금주: {b.account_holder}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom logos */}
      {companyInfo?.extra_bottom_images && companyInfo.extra_bottom_images.length > 0 && (
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              {companyInfo.extra_bottom_images.map((img, idx) => (
                <img key={idx} src={img} alt={`partner-${idx}`} className="h-10 md:h-12 object-contain opacity-70" />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Brand & Description */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: clientConfig.logoIconColor }}
              >
                <span className="text-white font-black text-xs">{clientConfig.logoIconText}</span>
              </div>
              <span className="text-base font-extrabold tracking-tight text-star-navy dark:text-white">
                {clientConfig.brandName}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm">
              AI 기반의 스마트한 복권 번호 생성과 과학적인 통계 분석으로
              더 나은 선택을 돕습니다.
            </p>
          </div>

          {/* Service Links */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">서비스</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/results" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  당첨결과 조회
                </Link>
              </li>
              <li>
                <Link to="/analysis" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  번호 분석
                </Link>
              </li>
              <li>
                <Link to="/plans" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  구독 플랜
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  마이페이지
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">고객지원</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/faq" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  자주 묻는 질문
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  이용약관
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-gray-600 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors">
                  개인정보처리방침
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">연락처</h4>
            <div className="space-y-2.5">
              {companyInfo?.contact_email && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                  <span>{companyInfo.contact_email}</span>
                </div>
              )}
              {companyInfo?.contact_number && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                  <span>{companyInfo.contact_number}</span>
                </div>
              )}
              {companyInfo?.contact_number_secondary && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                  <span>고객센터: {companyInfo.contact_number_secondary}</span>
                </div>
              )}
              {companyInfo?.working_hours && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                  <span>{companyInfo.working_hours}</span>
                </div>
              )}
              {companyInfo?.address && (
                <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0 mt-0.5" />
                  <span>{companyInfo.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-100 dark:border-gray-800 mt-10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-1">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                © {new Date().getFullYear()} {companyInfo?.name || clientConfig.copyrightFallback}. All rights reserved.
              </p>
              {companyInfo?.business_registration_number && (
                <p className="text-xs text-gray-400 dark:text-gray-500">사업자번호: {companyInfo.business_registration_number}</p>
              )}
              {companyInfo?.ecommerce_registration_number && (
                <p className="text-xs text-gray-400 dark:text-gray-500">통신판매신고번호: {companyInfo.ecommerce_registration_number}</p>
              )}
              {companyInfo?.owner && (
                <p className="text-xs text-gray-400 dark:text-gray-500">대표자: {companyInfo.owner}</p>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              본 서비스는 만 19세 이상만 이용 가능합니다.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default StarFooter
